import { Injectable } from '@angular/core';
import { TranslateParser } from '@ngx-translate/core';
import { ngSanitize } from './ng-sanitize.util';

/**
 * A ngx-translate `TranslateParser` that reproduces AngularJS angular-translate's
 * output byte-for-byte, so native components produce strings identical to the frozen
 * angular-translate baseline. Two things to match (verified by the full-dictionary
 * equivalence sweep, `account-parity/_diag-translate-equiv.mjs`):
 *
 *  1. INTERPOLATION — same `{{ token }}` matcher + dotted-path `getValue` as ngx's
 *     `TranslateDefaultParser`, EXCEPT a missing/undefined/null token renders as the
 *     EMPTY STRING (what AngularJS `$interpolate` does), not the literal `{{token}}`
 *     ngx leaves behind. (The dictionary uses only simple `{{id}}` / `{{a.b}}` — no
 *     expressions / ternaries / MessageFormat — so this is the whole surface.)
 *
 *  2. SANITIZE — the account config is `useSanitizeValueStrategy('sanitize')`, which
 *     runs every INTERPOLATED value through `$sanitize` (ngSanitize: `&`->`&amp;`,
 *     `\n`->`&#10;`, void-tag normalisation `<br>`->`<br/>`, allowlist). WS5 #10 Stage B
 *     B5(a): this no longer bridges to the AngularJS `$sanitize` via the upgrade-provided
 *     `$injector` — it now calls the NATIVE `ngSanitize` (`./ng-sanitize.util`), a
 *     byte-faithful port of the SAME bundled textAngular-`$sanitize` algorithm, so native
 *     translation renders survive the B6 boot-stop. Byte-equivalence over the WHOLE `en`
 *     dictionary (incl. the 21 HTML entries) is proven by `_diag-translate-equiv.mjs`
 *     (live `$filter('translate')` vs `window.__ngxTranslate.instant`, 0 mismatches) —
 *     identical bytes ⇒ NO re-baseline needed.
 */
@Injectable()
export class AngularTranslateCompatParser implements TranslateParser {
  templateMatcher: RegExp = /{{\s?([^{}\s]*)\s?}}/g;

  interpolate(expr: string | Function, params?: any): string {
    if (typeof expr === 'string') {
      return this.sanitize(this.interpolateString(expr, params));
    }
    if (typeof expr === 'function') {
      return (expr as Function)(params);
    }
    return expr as unknown as string;
  }

  getValue(target: any, key: string): any {
    const keys = typeof key === 'string' ? key.split('.') : [key];
    let k = '';
    do {
      k += keys.shift();
      if (target !== undefined && target !== null && target[k] !== undefined && target[k] !== null &&
        (typeof target[k] === 'object' || !keys.length)) {
        target = target[k];
        k = '';
      } else if (!keys.length) {
        target = undefined;
      } else {
        k += '.';
      }
    } while (keys.length);
    return target;
  }

  private sanitize(value: string): string {
    if (typeof value !== 'string') return value;
    return ngSanitize(value);
  }

  private interpolateString(expr: string, params?: any): string {
    return expr.replace(this.templateMatcher, (_substring: string, b: string) => {
      const r = params ? this.getValue(params, b) : undefined;
      // angular-translate `$interpolate` renders undefined/null as '' (ngx's default
      // parser would keep the literal `{{token}}` here).
      return r !== undefined && r !== null ? r : '';
    });
  }
}
